// vendor — pamäť učenia (learning loop bez ML).
import { db } from './db';

// lower-cased, orezaný counterparty_name
export function normalizeName(name) {
  return String(name || '').trim().toLowerCase();
}

// PK kľúč: IČO ak je, inak name_normalized.
function vendorKey({ ico, name }) {
  const cleanIco = String(ico || '').trim();
  if (cleanIco) return { type: 'ico', value: cleanIco };
  const nn = normalizeName(name);
  if (nn) return { type: 'name', value: nn };
  return null;
}

// Načítaj vendor podľa IČO alebo normalizovaného mena.
export async function findVendor({ ico, name }) {
  const cleanIco = String(ico || '').trim();
  if (cleanIco) {
    const v = await db.vendors.get(cleanIco);
    if (v) return v;
  }
  const nn = normalizeName(name);
  if (nn) {
    return await db.vendors.where('name_normalized').equals(nn).first();
  }
  return null;
}

// Upsert vendor po uložení záznamu (AC15).
// Posledná voľba kategórie vyhráva, confirm_count++.
// P5 (learning loop): pred prepisom uloží prev_category_id + prev_confirm_count
// (snapshot pred-stavu) aby unlearnVendor vedel Undo presne vrátiť. Tieto polia
// idú do hodnoty záznamu, NIE do indexu → žiadna db.version() zmena.
export async function learnVendor({ ico, name, category_id }) {
  if (!category_id) return; // bez kategórie sa nič neučí
  const key = vendorKey({ ico, name });
  if (!key) return;

  const now = new Date().toISOString();
  const nn = normalizeName(name);
  const cleanIco = String(ico || '').trim();

  const existing = await findVendor({ ico: cleanIco, name });
  if (existing) {
    await db.vendors.put({
      ...existing,
      // ak predtým nemal IČO a teraz prišlo, doplň ho
      ico: existing.ico || cleanIco || existing.ico,
      name_normalized: nn || existing.name_normalized,
      // Snapshot pred-stavu pre Undo (P5). Zachytí to, čo platilo PRED týmto zápisom.
      prev_category_id: existing.default_category_id ?? null,
      prev_confirm_count: existing.confirm_count ?? 0,
      default_category_id: category_id,
      confirm_count: (existing.confirm_count || 0) + 1,
      last_used_at: now,
      // LWW stamp (F1) — bez neho mergeRecord padne na "keep local" a naučený
      // dodávateľ sa cez zariadenia nezosynchronizuje (Dexie=zdroj, Drive=mirror).
      updated_at: now,
    });
  } else {
    await db.vendors.put({
      ico: cleanIco || nn, // PK: IČO ak je, inak name_normalized
      name_normalized: nn,
      // Nový vendor → pred-stav neexistoval (Undo vendor odstráni).
      prev_category_id: null,
      prev_confirm_count: null,
      default_category_id: category_id,
      confirm_count: 1,
      last_used_at: now,
      // LWW stamp (F1) — viď komentár vyššie.
      updated_at: now,
    });
  }
}

// Undo posledného learnVendor (P5). Vráti vendor do stavu pred posledným zápisom:
//   - ak mal pred-stav (prev_category_id != null) → obnoví default_category_id +
//     confirm_count na prev_* hodnoty, prev_* vyčistí
//   - ak pred-stav neexistoval (vendor vznikol týmto zápisom) → odstráni vendor záznam
// Samotný výdavok sa NEDOTÝKA — Undo ruší len naučené pravidlo.
// Vracia { undone: true, removed?: bool } alebo { undone: false } ak niet čo vrátiť.
export async function unlearnVendor({ ico, name }) {
  const existing = await findVendor({ ico, name });
  if (!existing) return { undone: false };

  const pkValue = existing.ico; // PK = ico pole (IČO alebo name_normalized fallback)

  if (existing.prev_category_id != null) {
    await db.vendors.put({
      ...existing,
      default_category_id: existing.prev_category_id,
      confirm_count: existing.prev_confirm_count ?? 0,
      prev_category_id: null,
      prev_confirm_count: null,
      // LWW stamp (F1) — Undo je tiež zmena dodávateľa, musí sa rozšíriť na ostatné zariadenia.
      updated_at: new Date().toISOString(),
    });
    return { undone: true, removed: false };
  }

  // Žiadny pred-stav → vendor vznikol posledným učením. Riadok NEMAŽEME natvrdo:
  // vendors je synchronizovaný store a outbox trigger je len AFTER INSERT/UPDATE,
  // takže hard delete by na Drive nikdy nedorazil a pull by dodávateľa oživil
  // (regresia J9 v syncSafety.realdb). Namiesto toho vyprázdnime naučené pole —
  // classify.js berie vendora ako naučeného iba pri neprázdnom default_category_id,
  // a UPDATE sa cez existujúci trigger rozšíri na ostatné zariadenia.
  await db.vendors.put({
    ...existing,
    ico: pkValue,
    default_category_id: null,
    confirm_count: 0,
    prev_category_id: null,
    prev_confirm_count: null,
    updated_at: new Date().toISOString(),
  });
  return { undone: true, removed: true };
}

// SK veta pre Proposal kartu — vysvetlí PREČO appka navrhla kategóriu (P5).
//   category_src='learned' → „lebo si to tak naposledy uložil"
//   category_src='rule'    → „<Dodávateľ> = <kategória>"
// catName = ľudský názov kategórie (name_sk) ak ho volajúci pozná, inak fallback.
// Vracia null pre 'user'/null (žiadny návrh = žiadny dôvod).
export function describeSuggestionReason({ name, category_src, catName } = {}) {
  if (category_src === 'learned') {
    return 'lebo si to tak naposledy uložil';
  }
  if (category_src === 'rule') {
    const who = String(name || '').trim() || 'Dodávateľ';
    const what = String(catName || '').trim();
    return what ? `${who} = ${what}` : `Pravidlo pre „${who}"`;
  }
  return null;
}
